import type { AgentSidebarSuggestion } from "@/components/agent-conversation/AgentConversationSidebar";
import type { AgentContextItem as AgentAttachableContextItem } from "@/components/agent-conversation/agent-context-items";
import type { StashedTemplateInputValues } from "@/components/agent-conversation/template-inputs";
import type { NotebookAgentMentionKind } from "@/components/notebook/menus/agent-mention-search";
import type { RunTurnRequestSchema } from "@/gen_src/proto/chalk/agent/v1/runner_pb";
import { useLocalStorageSyncedState } from "@/utils/hooks/use-local-storage-synced-state";
import { LocalStorageKey } from "@/utils/local-storage-utils";
import type { JsonObject, MessageInitShape } from "@bufbuild/protobuf";
import React, {
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import {
  assistantWorkspaceReducer,
  type AssistantChat,
  type AssistantWorkspaceAction,
  type AssistantWorkspaceState,
} from "./assistant-workspace-state";

// The environment-scoped workspace outlives pages. Existing notebook entry
// points address its focused chat; each panel gets its own explicit scope.

// A notebook entity attached to the conversation as context: an @-mention
// from the agent prompt bar. Serialized into the conversation metadata so the
// agent runner can ground the turn in the referenced cells/tables/variables.
export interface AgentContextItem {
  kind: NotebookAgentMentionKind;
  /** The mention target's stable id (cell id, variable name, feature FQN,
   *  ...), so the runner can hand the agent something it can look up. */
  id: string;
  name: string;
  /** Short secondary descriptor from the mention menu (cell kind, python
   *  type, dataframe shape). */
  detail?: string;
  /** The cell that defines this item, when known (the cell itself for cell
   *  mentions; the producing cell for tables/variables). */
  cellId?: string;
}

// Identifies the surface (currently only notebook cells) the agent is being
// asked about. Carried so the sidebar can display which cell is in focus and
// seed a real conversation with that context. The per-cell "ask agent" button
// sets the cell fields; the command bar's Agent entry sets contextItems
// instead (there is no single focused cell).
export interface AgentActiveContext {
  surface: "notebook";
  notebookId: string;
  cellId?: string;
  cellName?: string;
  language?: string;
  source?: string;
  contextItems?: AgentContextItem[];
  // Pins the conversation the sidebar creates to an agent template (system
  // prompt, model, tool allowlist). Set by the notebook-agent kickoff flow;
  // the ad-hoc notebook entry points leave it unset.
  agentTemplateId?: string;
  // Values for the pinned template's declared inputs, carried from the
  // kickoff and sent with CreateConversation.
  templateInputs?: StashedTemplateInputValues;
}

// A diff the agent proposes for a single cell. The cell whose id matches
// renders an in-place review UI (before/after diff + Undo/Confirm) instead of
// its normal source editor. This is a one-slot channel: only one proposed diff
// is live at a time, which matches the single-cell review flow in the mockup.
export interface AgentProposedCellDiff {
  notebookId?: string;
  cellId: string;
  before: string;
  after: string;
}

// The cell kinds the agent may propose adding through add_notebook_cell.
export type AgentProposedCellKind = "python" | "sql" | "text" | "markdown" | "input";

// A new cell the agent proposes inserting. The notebook editor renders a
// review card at the proposed position (before/after the anchor cell, or at
// the end when neither anchor is set); confirming inserts the cell into the
// local document, which autosave then persists. Source cells populate `source`;
// input cells populate `inputCell` with a chalk.notebook.v1.NotebookInputCell-
// shaped JSON object.
export interface AgentProposedCellAdd {
  notebookId?: string;
  kind: AgentProposedCellKind;
  source?: string;
  inputCell?: Record<string, unknown>;
  name?: string;
  beforeCellId?: string;
  afterCellId?: string;
}

// A cell the agent proposes deleting. The matching cell renders a delete
// review banner; confirming removes the cell from the local document.
export interface AgentProposedCellDelete {
  notebookId?: string;
  cellId: string;
}

// The per-turn fields a host page adds to RunTurn: the surface the turn is
// submitted from and whatever live page state the runner cannot read on its
// own. The session fills in the conversation and model fields itself.
export type AgentTurnContextInit = Omit<
  MessageInitShape<typeof RunTurnRequestSchema>,
  "$typeName" | "conversationId" | "model" | "maxIterations"
>;

// What the page hosting the assistant contributes to its chats, registered
// for as long as the page is mounted. Its presence is what turns every
// assistant chat into that page's agent: the page's items sit in each chat's
// "+ Context" tray as defaults, a turn sent with one of them attached carries
// the page's turn context (which can promote the turn to another PageContext),
// a conversation whose first turn carries one is scoped by the page's
// metadata, and tool results are offered to the page so it can pick out the
// changes the agent proposes for it. Taking the items off the tray makes that
// turn a plain assistant turn. Nothing about the surface is decided by the
// route string — the registration is the signal, and it disappears with the
// page.
//
// It is workspace-level rather than per-chat because the page has one editor
// (one worksheet, one notebook), whichever chat is asking about it. For the
// same reason the page, not the workspace, holds any change awaiting review:
// its editor can review one at a time regardless of which chat proposed it.
//
// The turn context and metadata are behind getters, not values: page state
// such as editor text and diagnostics changes on every keystroke and nothing
// here renders it, so a chat reads it once, at the moment a turn is sent.
// Only the rarely-changing display fields are values.
export interface AgentHostContextSource {
  /** Attached to every turn by default and listed under "On this page" in
   *  the tray. A turn carries the host context when its text mentions any of
   *  these (see mentionsAnyAgentContextItem). */
  contextItems: readonly AgentAttachableContextItem[];
  /** Shown as the chat header while the conversation has no title of its
   *  own, e.g. the name of the worksheet in focus. */
  focusedName?: string;
  /** Header title for an untitled chat composed with the host context
   *  attached. Falls back to the generic "New chat". */
  attachedTitle?: string;
  /** Suggestion chips shown in place of the generic ones while the host
   *  context is attached. */
  suggestions?: AgentSidebarSuggestion[];
  /** Metadata for a conversation whose first turn carries the host context.
   *  Written once at CreateConversation, so only stable identity belongs
   *  here; anything that moves between turns goes in getTurnContext. */
  getConversationMetadata: () => JsonObject | undefined;
  /** The live page state for a turn about to run, re-read at send time.
   *  Undefined when the page has nothing to add, in which case the turn goes
   *  out with the session's own page context. */
  getTurnContext: () => AgentTurnContextInit | undefined;
  /** Offered the structured content of every successful tool result, from
   *  any chat. The page parses the payloads of its own proposal tools (e.g.
   *  propose_sql_worksheet_edit) and stages the change for review; it
   *  returns true when it claimed the result, which stops the generic
   *  notebook proposal parsers from also inspecting it. */
  proposeChange?: (toolResult: unknown) => boolean;
}

interface AgentSidebarContextValue {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  // The cell/notebook the agent is currently focused on, or null when nothing
  // is in focus. Set by the per-cell "ask agent" affordance.
  activeContext: AgentActiveContext | null;
  setActiveContext: (context: AgentActiveContext | null) => void;
  // One-shot prompt handed off by the notebook's agent prompt bar: the docked
  // sidebar consumes it (submits it as the next user message) once it is open,
  // then clears it. Distinct from activeContext, which persists for the whole
  // conversation and only scopes metadata.
  pendingPrompt: string | null;
  setPendingPrompt: (prompt: string | null) => void;
  clearPendingPrompt: () => void;
  // An existing conversation the docked sidebar should adopt (load its
  // persisted history and continue it) instead of creating a fresh one. Set by
  // hosts that know a relevant conversation id — e.g. the notebook toolbar's
  // "linked agent conversation" button. Stays set while that conversation is
  // being viewed; cleared when the user starts a new conversation.
  requestedConversationId: string | null;
  requestConversation: (conversationId: string) => void;
  clearRequestedConversation: () => void;
  // The pending proposed diff, or null when no diff is being reviewed.
  proposedCellDiff: AgentProposedCellDiff | null;
  proposeCellDiff: (diff: AgentProposedCellDiff) => void;
  clearProposedCellDiff: () => void;
  // The pending proposed cell insertion — same one-slot channel semantics as
  // proposedCellDiff.
  proposedCellAdd: AgentProposedCellAdd | null;
  proposeCellAdd: (proposal: AgentProposedCellAdd) => void;
  clearProposedCellAdd: () => void;
  // The pending proposed cell deletion — same one-slot channel semantics as
  // proposedCellDiff.
  proposedCellDelete: AgentProposedCellDelete | null;
  proposeCellDelete: (proposal: AgentProposedCellDelete) => void;
  clearProposedCellDelete: () => void;
  // The host page's context, or null on pages that contribute none. Set by
  // the page on mount and cleared on unmount. Workspace-level: every chat
  // scope sees the same registration.
  hostContextSource: AgentHostContextSource | null;
  setHostContextSource: (source: AgentHostContextSource | null) => void;
}

const AgentSidebarContext = React.createContext<AgentSidebarContextValue | undefined>(undefined);

type AgentSessionContextValue = Omit<AgentSidebarContextValue, "isOpen">;
const AgentSessionContext = React.createContext<AgentSessionContextValue | undefined>(undefined);

// Session data and notebook proposals do not subscribe to panel visibility.
export const useAgentSessionContext = () => useContext(AgentSessionContext);

/** A notebook can review only its own proposals; the workspace keeps the others
 *  so opening their target notebook exposes them without losing the pending change. */
export const AgentNotebookContextScope: React.FC<PropsWithChildren<{ notebookId: string }>> = ({
  notebookId,
  children,
}) => {
  const session = useAgentSessionContext();
  const scopedSession = useMemo(() => {
    if (session == null) return undefined;
    const targetsNotebook = (proposal: { notebookId?: string } | null) =>
      proposal != null && (proposal.notebookId ?? session.activeContext?.notebookId) === notebookId;
    return {
      ...session,
      proposedCellDiff: targetsNotebook(session.proposedCellDiff) ? session.proposedCellDiff : null,
      proposedCellAdd: targetsNotebook(session.proposedCellAdd) ? session.proposedCellAdd : null,
      proposedCellDelete: targetsNotebook(session.proposedCellDelete)
        ? session.proposedCellDelete
        : null,
    };
  }, [notebookId, session]);
  return (
    <AgentSessionContext.Provider value={scopedSession}>{children}</AgentSessionContext.Provider>
  );
};

interface AgentWorkspaceContextValue {
  available: boolean;
  state: AssistantWorkspaceState;
  dispatch: React.Dispatch<AssistantWorkspaceAction>;
  newChat: () => void;
  // Opens an existing conversation as a new chat tab whose window replaces
  // the open one; a chat already showing that conversation is focused instead.
  openConversationChat: (conversationId: string) => void;
  focusChat: (id: string) => void;
  closeChat: (id: string) => void;
  updateChat: (id: string, patch: Partial<Omit<AssistantChat, "id">>) => void;
  railBounds: { left: number; width: number } | null;
  setRailBounds: React.Dispatch<React.SetStateAction<{ left: number; width: number } | null>>;
  hostContextSource: AgentHostContextSource | null;
  setHostContextSource: (source: AgentHostContextSource | null) => void;
}

const AgentWorkspaceContext = React.createContext<AgentWorkspaceContextValue | undefined>(
  undefined,
);

type AgentWorkspaceControls = Pick<
  AgentWorkspaceContextValue,
  | "available"
  | "dispatch"
  | "newChat"
  | "openConversationChat"
  | "focusChat"
  | "closeChat"
  | "updateChat"
>;
const AgentWorkspaceControlsContext = React.createContext<AgentWorkspaceControls | undefined>(
  undefined,
);
export const useAgentWorkspaceControls = () => useContext(AgentWorkspaceControlsContext);

export const useAgentWorkspace = () => useContext(AgentWorkspaceContext);

const EMPTY_WORKSPACE_STATE: AssistantWorkspaceState = { chats: [], activeId: null };

// Module-level so the hook's default identity is stable across renders.
const DEFAULT_FOCUSED_CONVERSATION_PREFERENCE: {
  conversationIdByEnvironment: Record<string, string>;
} = { conversationIdByEnvironment: {} };

// The conversation shown in the focused, non-minimized chat, or null when no
// chat is open (or the focused chat has not created its conversation yet).
const getFocusedConversationId = (state: AssistantWorkspaceState): string | null => {
  const active = state.chats.find((chat) => chat.id === state.activeId);
  return active != null && !active.minimized ? active.conversationId : null;
};

// Persists the focused conversation per environment and, once per mount,
// reopens the stored one. Without an environmentId (test pages, storybook-style
// harnesses) nothing is stored or restored.
const useFocusedConversationPersistence = (
  environmentId: string | undefined,
  state: AssistantWorkspaceState,
  dispatch: React.Dispatch<AssistantWorkspaceAction>,
) => {
  const [preference, setPreference] = useLocalStorageSyncedState(
    LocalStorageKey.ASSISTANT_FOCUSED_CONVERSATION,
    DEFAULT_FOCUSED_CONVERSATION_PREFERENCE,
  );
  const storedConversationId =
    environmentId != null ? preference.conversationIdByEnvironment[environmentId] ?? null : null;

  // The stored id is only known on the client (the server snapshot is the
  // default), so the restore is an effect rather than the reducer's initial
  // state. "dispatched" holds off persistence until the requested chat is in
  // state; otherwise the first commit would overwrite the stored id with null.
  const restoreRef = useRef<
    { phase: "pending" } | { phase: "dispatched"; conversationId: string } | { phase: "settled" }
  >({ phase: "pending" });
  useEffect(() => {
    if (restoreRef.current.phase !== "pending" || environmentId == null) return;
    if (storedConversationId == null) {
      restoreRef.current = { phase: "settled" };
      return;
    }
    restoreRef.current = { phase: "dispatched", conversationId: storedConversationId };
    dispatch({ type: "request", id: crypto.randomUUID(), conversationId: storedConversationId });
  }, [dispatch, environmentId, storedConversationId]);

  const focusedConversationId = getFocusedConversationId(state);
  useEffect(() => {
    if (environmentId == null) return;
    const restore = restoreRef.current;
    if (restore.phase === "pending") return;
    if (restore.phase === "dispatched") {
      if (!state.chats.some((chat) => chat.conversationId === restore.conversationId)) return;
      restoreRef.current = { phase: "settled" };
    }
    if (focusedConversationId === storedConversationId) return;
    setPreference((previous) => {
      const conversationIdByEnvironment = { ...previous.conversationIdByEnvironment };
      if (focusedConversationId == null) {
        delete conversationIdByEnvironment[environmentId];
      } else {
        conversationIdByEnvironment[environmentId] = focusedConversationId;
      }
      return { conversationIdByEnvironment };
    });
  }, [environmentId, focusedConversationId, setPreference, state.chats, storedConversationId]);
};

export const AgentSidebarProvider: React.FC<
  PropsWithChildren<{ available?: boolean; environmentId?: string }>
> = ({ children, available = true, environmentId }) => {
  const [state, dispatch] = useReducer(assistantWorkspaceReducer, EMPTY_WORKSPACE_STATE);
  useFocusedConversationPersistence(environmentId, state, dispatch);
  const [railBounds, setRailBounds] = useState<{ left: number; width: number } | null>(null);
  const [hostContextSource, setHostContextSource] = useState<AgentHostContextSource | null>(null);
  const actions = useMemo(
    () => ({
      newChat: () => dispatch({ type: "new", id: crypto.randomUUID() }),
      openConversationChat: (conversationId: string) =>
        dispatch({ type: "request", id: crypto.randomUUID(), conversationId }),
      focusChat: (id: string) => dispatch({ type: "focus", id }),
      closeChat: (id: string) => dispatch({ type: "close", id }),
      updateChat: (id: string, patch: Partial<Omit<AssistantChat, "id">>) =>
        dispatch({ type: "update", id, fallbackId: id, patch }),
    }),
    [],
  );
  const value = useMemo(
    () => ({
      available,
      state,
      dispatch,
      ...actions,
      railBounds,
      setRailBounds,
      hostContextSource,
      setHostContextSource,
    }),
    [available, state, actions, railBounds, hostContextSource],
  );
  const controls = useMemo(() => ({ available, dispatch, ...actions }), [available, actions]);
  return (
    <AgentWorkspaceControlsContext.Provider value={controls}>
      <AgentWorkspaceContext.Provider value={value}>
        <AgentSidebarScope>{children}</AgentSidebarScope>
      </AgentWorkspaceContext.Provider>
    </AgentWorkspaceControlsContext.Provider>
  );
};

// Each mounted session reads its own tab, while notebook entry points target the focused tab.
export const AgentSidebarScope: React.FC<PropsWithChildren<{ chatId?: string }>> = ({
  chatId,
  children,
}) => {
  const workspace = useAgentWorkspace();
  if (workspace == null) throw new Error("AgentSidebarScope requires AgentSidebarProvider");
  const { state, dispatch, hostContextSource, setHostContextSource } = workspace;
  const chat = state.chats.find((item) => item.id === (chatId ?? state.activeId));
  const actions = useMemo(() => {
    const update = (patch: Partial<Omit<AssistantChat, "id">>) =>
      dispatch({ type: "update", id: chatId, fallbackId: crypto.randomUUID(), patch });
    return {
      open: () => update({ minimized: false }),
      close: () => update({ minimized: true }),
      toggle: () => dispatch({ type: "toggle", id: chatId, fallbackId: crypto.randomUUID() }),
      setActiveContext: (activeContext: AgentActiveContext | null) => update({ activeContext }),
      setPendingPrompt: (pendingPrompt: string | null) => update({ pendingPrompt }),
      clearPendingPrompt: () => update({ pendingPrompt: null }),
      requestConversation: (conversationId: string) =>
        dispatch({ type: "request", id: crypto.randomUUID(), conversationId }),
      clearRequestedConversation: () => update({ requestedConversationId: null }),
      proposeCellDiff: (proposedCellDiff: AgentProposedCellDiff) => update({ proposedCellDiff }),
      clearProposedCellDiff: () => update({ proposedCellDiff: null }),
      proposeCellAdd: (proposedCellAdd: AgentProposedCellAdd) => update({ proposedCellAdd }),
      clearProposedCellAdd: () => update({ proposedCellAdd: null }),
      proposeCellDelete: (proposedCellDelete: AgentProposedCellDelete) =>
        update({ proposedCellDelete }),
      clearProposedCellDelete: () => update({ proposedCellDelete: null }),
    };
  }, [chatId, dispatch]);
  const activeContext = chat?.activeContext ?? null;
  const pendingPrompt = chat?.pendingPrompt ?? null;
  const requestedConversationId = chat?.requestedConversationId ?? null;
  const proposedCellDiff = chat?.proposedCellDiff ?? null;
  const proposedCellAdd = chat?.proposedCellAdd ?? null;
  const proposedCellDelete = chat?.proposedCellDelete ?? null;
  const session = useMemo<AgentSessionContextValue>(
    () => ({
      ...actions,
      activeContext,
      pendingPrompt,
      requestedConversationId,
      proposedCellDiff,
      proposedCellAdd,
      proposedCellDelete,
      hostContextSource,
      setHostContextSource,
    }),
    [
      actions,
      activeContext,
      pendingPrompt,
      requestedConversationId,
      proposedCellDiff,
      proposedCellAdd,
      proposedCellDelete,
      hostContextSource,
      setHostContextSource,
    ],
  );
  const isOpen = chat != null && !chat.minimized;
  const value = useMemo<AgentSidebarContextValue>(
    () => ({ ...session, isOpen }),
    [session, isOpen],
  );
  return (
    <AgentSessionContext.Provider value={session}>
      <AgentSidebarContext.Provider value={value}>{children}</AgentSidebarContext.Provider>
    </AgentSessionContext.Provider>
  );
};

// Reads the sidebar context. Returns `undefined` when the provider is not in
// the tree — callers (the trigger button, the docked sidebar) check for this
// and render nothing, so the feature stays inert outside admin-only layouts.
export function useAgentSidebar(): AgentSidebarContextValue | undefined {
  return useContext(AgentSidebarContext);
}
